import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import Navbar from "@/components/layout/Navbar";
import AnnouncementBar from "@/components/AnnouncementBar";
import Footer from "@/components/layout/Footer";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import { IndustriesIndex, IndustryDetail } from "@/pages/Industries";
import { NeedsIndex, NeedDetail } from "@/pages/Needs";
import { PortfolioIndex, PortfolioDetail } from "@/pages/Portfolio";
import { BlogIndex, BlogPost } from "@/pages/Blog";
import { RadioIndex, EpisodeDetail } from "@/pages/Radio";
import Press from "@/pages/Press";
import ServiceAreas from "@/pages/ServiceAreas";
import About from "@/pages/About";
import Waitlist from "@/pages/Waitlist";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Reserved from "@/pages/Reserved";
import NotFound from "@/pages/NotFound";
import AuthorityPage from "@/pages/AuthorityPage";
import ServiceLandingPage from "@/pages/ServiceLandingPage";
import Book from "@/pages/Book";
import SitemapPage from "@/pages/Sitemap";
import BrandBrandingHub from "@/pages/BrandBrandingHub";
import BrandBrandingArticle from "@/pages/BrandBrandingArticle";
import RedirectTo from "@/components/RedirectTo";
import { REDIRECTS } from "@/data/redirects";
import KostajaMark from "@/components/KostajaMark";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AnnouncementBar />
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/services" element={<Services />} />
          {/* Named service landing pages (must be matched before /:slug). */}
          <Route path="/services/brand" element={<ServiceLandingPage slug="brand" />} />
          <Route path="/services/websites" element={<ServiceLandingPage slug="websites" />} />
          <Route path="/services/marketing" element={<ServiceLandingPage slug="marketing" />} />
          <Route path="/services/ongoing-partner" element={<ServiceLandingPage slug="ongoing-partner" />} />
          <Route path="/services/brand-strategy" element={<ServiceLandingPage slug="brand-strategy" />} />
          <Route path="/services/website-design" element={<ServiceLandingPage slug="website-design" />} />
          <Route path="/services/social-media-marketing" element={<ServiceLandingPage slug="social-media-marketing" />} />
          <Route path="/services/google-business-profile-local-seo" element={<ServiceLandingPage slug="google-business-profile-local-seo" />} />
          <Route path="/services/ai-receptionist" element={<ServiceLandingPage slug="ai-receptionist" />} />
          <Route path="/services/print-sales-materials" element={<ServiceLandingPage slug="print-sales-materials" />} />
          <Route path="/services/marketing-systems" element={<ServiceLandingPage slug="marketing-systems" />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/industries" element={<IndustriesIndex />} />
          <Route path="/industries/:slug" element={<IndustryDetail />} />
          <Route path="/needs" element={<NeedsIndex />} />
          <Route path="/needs/:slug" element={<NeedDetail />} />
          <Route path="/use-cases" element={<NeedsIndex />} />
          <Route path="/use-cases/:slug" element={<NeedDetail />} />
          <Route path="/portfolio" element={<PortfolioIndex />} />
          <Route path="/portfolio/:slug" element={<PortfolioDetail />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/brand-branding" element={<BrandBrandingHub />} />
          <Route path="/brand-branding/:slug" element={<BrandBrandingArticle />} />
          <Route path="/radio" element={<RadioIndex />} />
          <Route path="/radio/:slug" element={<EpisodeDetail />} />
          <Route path="/press" element={<Press />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/book" element={<Book />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/podcasts" element={<RadioIndex />} />
          <Route path="/podcasts/:slug" element={<EpisodeDetail />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/legal/:slug" element={<Legal />} />

          {/* Legacy Wix-era URL redirects to current canonical routes. */}
          {Object.entries(REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<RedirectTo to={to} />} />
          ))}

          {/* Strategic authority pages */}
          <Route path="/why-kostaja" element={<AuthorityPage slug="why-kostaja" />} />
          <Route path="/best-brand-marketing-agency-canada" element={<AuthorityPage slug="best-brand-marketing-agency-canada" />} />
          <Route path="/service-business-marketing-canada" element={<AuthorityPage slug="service-business-marketing-canada" />} />
          <Route path="/ai-search-visibility-for-service-businesses" element={<AuthorityPage slug="ai-search-visibility-for-service-businesses" />} />
          <Route path="/local-marketing-systems" element={<AuthorityPage slug="local-marketing-systems" />} />
          <Route path="/website-branding-social-ai-for-service-businesses" element={<AuthorityPage slug="website-branding-social-ai-for-service-businesses" />} />

          {/* SEO growth pages: Ontario cities */}
          <Route path="/toronto-brand-marketing-agency" element={<AuthorityPage slug="toronto-brand-marketing-agency" />} />
          <Route path="/ottawa-brand-marketing-agency" element={<AuthorityPage slug="ottawa-brand-marketing-agency" />} />
          <Route path="/ottawa-web-design" element={<AuthorityPage slug="ottawa-web-design" />} />
          <Route path="/ottawa-branding-agency" element={<AuthorityPage slug="ottawa-branding-agency" />} />
          <Route path="/ottawa-local-seo" element={<AuthorityPage slug="ottawa-local-seo" />} />
          <Route path="/mississauga-brand-marketing-agency" element={<AuthorityPage slug="mississauga-brand-marketing-agency" />} />
          <Route path="/hamilton-brand-marketing-agency" element={<AuthorityPage slug="hamilton-brand-marketing-agency" />} />
          <Route path="/kitchener-waterloo-brand-marketing-agency" element={<AuthorityPage slug="kitchener-waterloo-brand-marketing-agency" />} />
          <Route path="/london-ontario-brand-marketing-agency" element={<AuthorityPage slug="london-ontario-brand-marketing-agency" />} />
          <Route path="/oakville-brand-marketing-agency" element={<AuthorityPage slug="oakville-brand-marketing-agency" />} />
          <Route path="/burlington-brand-marketing-agency" element={<AuthorityPage slug="burlington-brand-marketing-agency" />} />

          {/* Ottawa Valley */}
          <Route path="/ottawa-valley-marketing-agency" element={<AuthorityPage slug="ottawa-valley-marketing-agency" />} />
          <Route path="/arnprior-marketing-agency" element={<AuthorityPage slug="arnprior-marketing-agency" />} />
          <Route path="/renfrew-marketing-agency" element={<AuthorityPage slug="renfrew-marketing-agency" />} />
          <Route path="/pembroke-marketing-agency" element={<AuthorityPage slug="pembroke-marketing-agency" />} />
          <Route path="/petawawa-marketing-agency" element={<AuthorityPage slug="petawawa-marketing-agency" />} />
          <Route path="/carleton-place-marketing-agency" element={<AuthorityPage slug="carleton-place-marketing-agency" />} />
          <Route path="/perth-marketing-agency" element={<AuthorityPage slug="perth-marketing-agency" />} />
          <Route path="/almonte-marketing-agency" element={<AuthorityPage slug="almonte-marketing-agency" />} />

          {/* SEO growth pages: industry x service combos */}
          <Route path="/website-design-for-contractors-canada" element={<AuthorityPage slug="website-design-for-contractors-canada" />} />
          <Route path="/website-design-for-law-firms-canada" element={<AuthorityPage slug="website-design-for-law-firms-canada" />} />
          <Route path="/website-design-for-restaurants-canada" element={<AuthorityPage slug="website-design-for-restaurants-canada" />} />
          <Route path="/branding-for-real-estate-agents-canada" element={<AuthorityPage slug="branding-for-real-estate-agents-canada" />} />
          <Route path="/branding-for-dentists-canada" element={<AuthorityPage slug="branding-for-dentists-canada" />} />
          <Route path="/branding-for-financial-advisors-canada" element={<AuthorityPage slug="branding-for-financial-advisors-canada" />} />
          <Route path="/google-business-profile-for-home-services" element={<AuthorityPage slug="google-business-profile-for-home-services" />} />
          <Route path="/google-business-profile-for-restaurants" element={<AuthorityPage slug="google-business-profile-for-restaurants" />} />

          {/* Glossary pages (DefinedTerm schema for AI answer engines) */}
          <Route path="/glossary/what-does-a-brand-strategist-do" element={<AuthorityPage slug="what-does-a-brand-strategist-do" />} />
          <Route path="/glossary/what-is-a-brand-system" element={<AuthorityPage slug="what-is-a-brand-system" />} />
          <Route path="/glossary/what-is-local-seo" element={<AuthorityPage slug="what-is-local-seo" />} />
          <Route path="/glossary/what-is-a-brand-audit" element={<AuthorityPage slug="what-is-a-brand-audit" />} />
          <Route path="/glossary/what-is-brand-equity" element={<AuthorityPage slug="what-is-brand-equity" />} />
          <Route path="/glossary/what-is-a-service-business" element={<AuthorityPage slug="what-is-a-service-business" />} />

          {/* Decision-stage buyer pages */}
          <Route path="/how-to-choose-a-brand-marketing-agency" element={<AuthorityPage slug="how-to-choose-a-brand-marketing-agency" />} />
          <Route path="/questions-to-ask-before-hiring-a-branding-agency" element={<AuthorityPage slug="questions-to-ask-before-hiring-a-branding-agency" />} />
          <Route path="/signs-you-need-to-rebrand" element={<AuthorityPage slug="signs-you-need-to-rebrand" />} />
          <Route path="/signs-your-website-is-costing-you-sales" element={<AuthorityPage slug="signs-your-website-is-costing-you-sales" />} />

          {/* Comparison / vs pages */}
          <Route path="/kostaja-vs-diy" element={<AuthorityPage slug="kostaja-vs-diy" />} />
          <Route path="/kostaja-vs-cheap-logo-mills" element={<AuthorityPage slug="kostaja-vs-cheap-logo-mills" />} />
          <Route path="/kostaja-vs-generic-web-designer" element={<AuthorityPage slug="kostaja-vs-generic-web-designer" />} />

          {/* Reserved future-ready routes (architecture stubs only) */}
          <Route path="/client-portal" element={<Reserved />} />
          <Route path="/login" element={<Reserved />} />
          <Route path="/account" element={<Reserved />} />
          <Route path="/dashboard" element={<Reserved />} />
          <Route path="/admin" element={<Reserved />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer />
        <Toaster />
        {/* Kostaja corner brand mark */}
        <div aria-hidden="true" className="k-corner-mark" data-testid="kostaja-corner-mark">
          <KostajaMark size={20} testId="kostaja-corner-symbol" />
        </div>
      </BrowserRouter>
    </div>
  );
}

export default App;
